/**
 * AtlasVRMenuModule — the in-headset PANEL TIER for Quest 3 / WebXR sessions.
 *
 * DOM UI never renders inside an immersive session, so this projects a paged
 * 2D panel onto a canvas-textured quad IN THE SCENE (the VirtualScreen idea,
 * self-contained) and drives it with the controllers:
 *
 *   X button (left controller)  toggle the panel — opens facing you, ~1.4 m
 *                               ahead at head height, then stays world-anchored
 *                               (a head-locked panel swims; comfort failure).
 *   point + trigger             either controller's ray hovers; trigger clicks.
 *
 * Pages (tabs across the top, ray-clickable):
 *   Main    — Go home · Go to the Commons · Exit VR
 *   Worlds  — your owned worlds (live from the replicated `world` table) with
 *             one-click Enter; creation/renaming stays on desktop (typing).
 *   HUD     — read-only status: wallet SOV/INF, the four Pulse needs as bars,
 *             time-of-day, players in this world (refreshes ~1 Hz while open).
 *
 * The footer mirrors the latest `atlas:status` line on every page, so reducer
 * feedback stays visible in-headset. Rendering rides the engine's XR
 * renderFrame path; the quad + controller rays parent to the avatar rig so
 * reference-space poses compose with movement.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { XRModule } from 'enginex/xr/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { drawIcon } from '../lib/icons.js';
import { withLineNormals } from '../lib/line_geometry.js';

const PANEL_W = 0.9; // metres
const PANEL_H = 0.66;
const CANVAS_W = 720;
const CANVAS_H = 528;
const TAB_Y = 64;
const TAB_H = 46;
const ROWS_TOP = 126;
const ROW_H = 58;

type PageId = 'main' | 'worlds' | 'hud';
const PAGES: Array<{ id: PageId; label: string }> = [
  { id: 'main', label: 'Main' },
  { id: 'worlds', label: 'My Worlds' },
  { id: 'hud', label: 'HUD' },
];

interface Row { icon: string; label: string; action: () => void }
type HitTarget = { kind: 'tab'; index: number } | { kind: 'row'; index: number } | null;

export class AtlasVRMenuModule implements IModule {
  readonly name = 'AtlasVRMenu';

  private scene!: THREE.Scene;
  private panel: THREE.Mesh | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private tex: THREE.CanvasTexture | null = null;
  private page: PageId = 'main';
  private rows: Row[] = [];
  private hovered: HitTarget = null;
  private visible = false;
  private prevX = false; // left X button edge latch
  private lastStatus = '';
  private hudTimer = 0;
  private controllers: THREE.Group[] = [];
  private rayLines: THREE.Line[] = [];
  private readonly raycaster = new THREE.Raycaster();
  private readonly tmpM = new THREE.Matrix4();

  constructor(private readonly xr: XRModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    bus.on('atlas:status', ({ text }) => {
      this.lastStatus = text;
      if (this.visible) this.redraw();
    });
    const xrm = app.renderer.native.xr;
    xrm.addEventListener('sessionstart', () => this.onSessionStart(app));
    xrm.addEventListener('sessionend', () => this.hide());
    app.world.addSystem(makeSystem('AtlasVRMenu', Phase.Input, 26, (dt) => this.poll(app, dt)));
  }

  // ─── Session wiring ───────────────────────────────────────────────────────────

  private onSessionStart(app: App): void {
    this.ensureControllers(app);
  }

  /**
   * Attach controllers + ray lines to the avatar rig. Retried from poll():
   * a session can legally start BEFORE AtlasAvatarModule has built the rig
   * (VR entered from the menu / a slow world boot), and the old single-shot
   * attempt permanently lost the controllers for that whole session.
   */
  private ensureControllers(app: App): void {
    if (this.controllers.length === 0) {
      const body = atlas.localMesh;
      if (!body) return;
      for (const i of [0, 1]) {
        const c = app.renderer.native.xr.getController(i);
        body.add(c);
        c.addEventListener('selectstart', () => this.onSelect(c));
        this.controllers.push(c);
        const geo = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(0, 0, -3),
        ]);
        const line = new THREE.Line(withLineNormals(geo), new THREE.LineBasicMaterial({ color: 0x7fd0ff, transparent: true, opacity: 0.6 }));
        line.visible = false;
        c.add(line);
        this.rayLines.push(line);
      }
    }
  }

  // ─── Panel lifecycle ──────────────────────────────────────────────────────────

  private show(): void {
    const cam = atlas.localMesh?.children[0] as THREE.PerspectiveCamera | undefined;
    if (!cam) return;
    if (!this.panel) {
      this.canvas = document.createElement('canvas');
      this.canvas.width = CANVAS_W;
      this.canvas.height = CANVAS_H;
      this.tex = new THREE.CanvasTexture(this.canvas);
      this.tex.colorSpace = THREE.SRGBColorSpace;
      const mat = new THREE.MeshBasicMaterial({ map: this.tex, transparent: true, depthTest: false });
      this.panel = new THREE.Mesh(new THREE.PlaneGeometry(PANEL_W, PANEL_H), mat);
      this.panel.renderOrder = 999;
      this.scene.add(this.panel);
    }
    const headPos = new THREE.Vector3();
    cam.getWorldPosition(headPos);
    const fwd = new THREE.Vector3();
    cam.getWorldDirection(fwd);
    fwd.y = 0;
    if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1);
    fwd.normalize();
    this.panel.position.copy(headPos).addScaledVector(fwd, 1.4);
    this.panel.lookAt(headPos);
    this.panel.visible = true;
    this.visible = true;
    for (const l of this.rayLines) l.visible = true;
    this.buildRows();
    this.redraw();
  }

  private hide(): void {
    this.visible = false;
    this.hovered = null;
    if (this.panel) this.panel.visible = false;
    for (const l of this.rayLines) l.visible = false;
  }

  private setPage(p: PageId): void {
    this.page = p;
    this.buildRows();
    this.redraw();
  }

  private buildRows(): void {
    const conn = atlas.conn;
    const me = atlas.identityHex;
    this.rows = [];
    if (this.page === 'main') {
      if (atlas.personalWorldId !== null && atlas.currentWorldId !== atlas.personalWorldId) {
        this.rows.push({ icon: 'home', label: 'Go home', action: () => this.enter(atlas.personalWorldId!) });
      }
      if (atlas.commonsWorldId !== null && atlas.currentWorldId !== atlas.commonsWorldId) {
        this.rows.push({ icon: 'globe', label: 'Go to the Commons', action: () => this.enter(atlas.commonsWorldId!) });
      }
      this.rows.push({ icon: 'exit', label: 'Exit VR', action: () => void this.xr.endSession() });
    } else if (this.page === 'worlds') {
      if (conn && me) {
        let n = 0;
        for (const w of conn.db.world.iter()) {
          if (n >= 6) break;
          if (w.owner.toHexString() !== me || w.kind === 'commons') continue;
          const here = w.worldId === atlas.currentWorldId;
          const wid = w.worldId;
          this.rows.push({
            icon: here ? 'pin' : 'arrow',
            label: `${w.name.slice(0, 24)}${here ? ' (here)' : ''}`,
            action: here ? () => { /* already here */ } : () => this.enter(wid),
          });
          n++;
        }
      }
      if (this.rows.length === 0) {
        this.rows.push({ icon: 'globe', label: '(no owned worlds replicated yet)', action: () => { /* info row */ } });
      }
    }
    // 'hud' has no rows — it paints data directly.
  }

  private enter(worldId: bigint): void {
    bus.emit('atlas:enter-world', { worldId });
    this.hide();
  }

  // ─── Per-frame: toggle button, hover, HUD refresh ─────────────────────────────

  private poll(app: App, dt: number): void {
    const xrm = app.renderer.native.xr;
    if (!xrm.isPresenting) return;
    const session = xrm.getSession();
    if (!session) return;
    this.ensureControllers(app); // rig may have been built after sessionstart

    let x = false;
    for (const src of session.inputSources) {
      if (src.handedness === 'left') x = src.gamepad?.buttons[4]?.pressed ?? false;
    }
    if (x && !this.prevX) {
      if (this.visible) this.hide();
      else this.show();
    }
    this.prevX = x;

    if (!this.visible || !this.panel) return;

    // HUD page repaints ~1 Hz (wallet/pulse/time drift without events).
    if (this.page === 'hud') {
      this.hudTimer += dt;
      if (this.hudTimer > 1) {
        this.hudTimer = 0;
        this.redraw();
      }
    }

    let hit: HitTarget = null;
    for (const c of this.controllers) {
      hit = this.hitAtRay(c);
      if (hit) break;
    }
    // Field compare, not JSON.stringify — this runs per frame at headset rate
    // and the old string round-trip allocated on every single frame.
    const a = hit;
    const b = this.hovered;
    if ((a === null) !== (b === null) || (a && b && (a.kind !== b.kind || a.index !== b.index))) {
      this.hovered = hit;
      this.redraw();
    }
  }

  private hitAtRay(controller: THREE.Object3D): HitTarget {
    if (!this.panel) return null;
    this.tmpM.identity().extractRotation(controller.matrixWorld);
    this.raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
    this.raycaster.ray.direction.set(0, 0, -1).applyMatrix4(this.tmpM);
    const hits = this.raycaster.intersectObject(this.panel, false);
    if (hits.length === 0 || !hits[0].uv) return null;
    const px = hits[0].uv.x * CANVAS_W;
    const py = (1 - hits[0].uv.y) * CANVAS_H;
    if (py >= TAB_Y && py <= TAB_Y + TAB_H) {
      const tabW = (CANVAS_W - 40) / PAGES.length;
      const i = Math.floor((px - 20) / tabW);
      if (i >= 0 && i < PAGES.length) return { kind: 'tab', index: i };
      return null;
    }
    if (this.page !== 'hud') {
      const i = Math.floor((py - ROWS_TOP) / ROW_H);
      if (i >= 0 && i < this.rows.length) return { kind: 'row', index: i };
    }
    return null;
  }

  private onSelect(controller: THREE.Object3D): void {
    if (!this.visible) return;
    const hit = this.hitAtRay(controller);
    if (!hit) return;
    if (hit.kind === 'tab') this.setPage(PAGES[hit.index].id);
    else this.rows[hit.index]?.action();
  }

  // ─── Canvas paint ─────────────────────────────────────────────────────────────

  private redraw(): void {
    if (!this.canvas || !this.tex) return;
    const g = this.canvas.getContext('2d')!;
    g.clearRect(0, 0, CANVAS_W, CANVAS_H);
    g.fillStyle = 'rgba(9,13,20,.92)';
    g.strokeStyle = '#35506a';
    g.lineWidth = 3;
    g.beginPath();
    g.roundRect(2, 2, CANVAS_W - 4, CANVAS_H - 4, 18);
    g.fill();
    g.stroke();

    // Header: title + current world
    const conn = atlas.conn;
    const world = conn && atlas.currentWorldId !== null ? conn.db.world.worldId.find(atlas.currentWorldId) : null;
    g.fillStyle = '#eaf6ff';
    g.font = '600 28px system-ui';
    g.fillText('Cosmos', 26, 42);
    g.fillStyle = '#9fc3e8';
    g.font = '22px system-ui';
    g.fillText(world ? world.name.slice(0, 34) : '…', 104, 42);

    // Tabs
    const tabW = (CANVAS_W - 40) / PAGES.length;
    PAGES.forEach((p, i) => {
      const x = 20 + i * tabW;
      const active = p.id === this.page;
      const hov = this.hovered?.kind === 'tab' && this.hovered.index === i;
      g.fillStyle = active ? 'rgba(46,109,164,.55)' : hov ? 'rgba(46,109,164,.28)' : 'rgba(255,255,255,.05)';
      g.beginPath();
      g.roundRect(x + 3, TAB_Y, tabW - 6, TAB_H, 9);
      g.fill();
      g.fillStyle = active ? '#ffffff' : '#c9d8ea';
      g.font = `${active ? '600 ' : ''}22px system-ui`;
      const tw = g.measureText(p.label).width;
      g.fillText(p.label, x + (tabW - tw) / 2, TAB_Y + 31);
    });

    if (this.page === 'hud') this.paintHud(g);
    else {
      g.font = '24px system-ui';
      this.rows.forEach((r, i) => {
        const y = ROWS_TOP + i * ROW_H;
        const hov = this.hovered?.kind === 'row' && this.hovered.index === i;
        if (hov) {
          g.fillStyle = 'rgba(46,109,164,.45)';
          g.beginPath();
          g.roundRect(16, y - 4, CANVAS_W - 32, ROW_H - 8, 10);
          g.fill();
        }
        const color = hov ? '#ffffff' : '#dde6f2';
        drawIcon(g, r.icon, 34, y + 8, 28, hov ? '#ffffff' : '#8fc3ef');
        g.fillStyle = color;
        g.fillText(r.label, 76, y + 34);
      });
    }

    if (this.lastStatus) {
      g.fillStyle = '#8fb8dd';
      g.font = '17px system-ui';
      g.fillText(this.lastStatus.slice(0, 68), 26, CANVAS_H - 18);
    }
    this.tex.needsUpdate = true;
  }

  /** HUD page: wallet, Pulse bars, time-of-day, players here — read-only mirrors. */
  private paintHud(g: CanvasRenderingContext2D): void {
    const conn = atlas.conn;
    const me = atlas.identityHex;
    let y = ROWS_TOP + 8;

    // Wallet
    let sov = '—';
    let inf = '—';
    if (conn && me) {
      for (const w of conn.db.wallet.iter()) {
        if (w.identity.toHexString() === me) { sov = w.sov.toString(); inf = w.inf.toString(); break; }
      }
    }
    g.font = '600 24px system-ui';
    drawIcon(g, 'diamond', 34, y - 2, 26, '#ffd27f');
    g.fillStyle = '#ffd27f';
    g.fillText(`${sov} SOV`, 68, y + 20);
    drawIcon(g, 'star', 260, y - 2, 26, '#9cffc4');
    g.fillStyle = '#9cffc4';
    g.fillText(`${inf} INF`, 294, y + 20);
    y += 56;

    // Pulse bars (Social / Fun / Creativity / Rest)
    interface PulseRow { social: number; fun: number; creativity: number; rest: number }
    let pulse: PulseRow | null = null;
    if (conn && me) {
      const tbl = (conn.db as unknown as { pulse?: { iter(): Iterable<PulseRow & { identity: { toHexString(): string } }> } }).pulse;
      if (tbl) for (const p of tbl.iter()) if (p.identity.toHexString() === me) { pulse = p; break; }
    }
    const needs: Array<[string, number]> = pulse
      ? [['Social', pulse.social], ['Fun', pulse.fun], ['Creativity', pulse.creativity], ['Rest', pulse.rest]]
      : [];
    g.font = '19px system-ui';
    for (const [label, v] of needs) {
      g.fillStyle = '#c9d8ea';
      g.fillText(label, 34, y + 16);
      g.fillStyle = 'rgba(255,255,255,.1)';
      g.beginPath();
      g.roundRect(160, y, 420, 20, 9);
      g.fill();
      g.fillStyle = '#5aa8e8';
      g.beginPath();
      g.roundRect(160, y, Math.max(10, 420 * Math.min(100, Math.max(0, v)) / 100), 20, 9);
      g.fill();
      y += 34;
    }
    if (!pulse) {
      g.fillStyle = '#8fa8c8';
      g.fillText('(Pulse not replicated yet)', 34, y + 16);
      y += 34;
    }
    y += 12;

    // Time of day + players here
    let tod = 0.5;
    let players = 0;
    if (conn && atlas.currentWorldId !== null) {
      tod = conn.db.skyState.worldId.find(atlas.currentWorldId)?.timeOfDay ?? 0.5;
      for (const p of conn.db.playerState.iter()) if (p.worldId === atlas.currentWorldId) players++;
    }
    const hours = Math.floor(tod * 24);
    const mins = Math.floor((tod * 24 - hours) * 60);
    g.fillStyle = '#dde6f2';
    g.font = '22px system-ui';
    drawIcon(g, 'clock', 34, y - 1, 24, '#8fc3ef');
    g.fillText(`${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')} in-world`, 66, y + 20);
    drawIcon(g, 'people', 320, y - 1, 24, '#8fc3ef');
    g.fillText(`${players} player${players === 1 ? '' : 's'} here`, 352, y + 20);
  }

  dispose(): void {
    this.hide();
    if (this.panel) this.scene.remove(this.panel);
    this.panel = null;
  }
}
